import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import useSession from 'core/hooks/use-session';
import { useFloatingPosition } from 'core/hooks/use-floating-position';
import { track } from 'core/helpers/analytics';
import { getStaffToolsChatContext } from './staff-tools-chat-context';
import useStyles from './staff-tools-chat.styles';

// The organization's path configuration selects Staff Tools Assist for the current host/route.
const INDEMN_SCRIPT_SRC = 'https://proxy.indemn.ai/chat-script/69b59901a89eea32b49183fa.js';
const NO_TOOL_CONTEXT = { view: 'no-tool' };
const CONTEXT_INSTRUCTIONS = [
  'The staff member is viewing this Staff Tool. Answer about this tool unless they name another one. Do not assume anything about specific records.',
  'Write for operations staff, not engineers. Lead with a short plain-language answer, and describe access in everyday terms, such as needing a permission group that allows this tool.',
  'Put exact permission names, Cognito groups, and other code identifiers only in a short "Technical details" section at the end. Always quote CSV headers exactly.',
  'If the guide lists a Guru SOP for the tool, include it as a Markdown link.'
].join(' ');
const INDEMN_EVENTS = [
  'chat:show',
  'chat:hide',
  'conversation:started',
  'conversation:ended',
  'conversation:restarted',
  'message:sent',
  'message:received',
  'suggestion:clicked',
  'payload:sent',
  'feedback:submitted',
  'launcher:click'
];
// Space the SDK leaves between the launcher and the conversation window; reused as the screen margin.
const WINDOW_GAP_PX = 8;
// The SDK's "powered by" tag hangs this far below the window, inset from the side away from the launcher.
const POWERED_BY_HEIGHT_PX = 29;
const POWERED_BY_INSET_PX = 16;

// CSS custom properties that move the SDK's fixed launcher and conversation window to the spot staff
// dragged the launcher to. The window opens on whichever side of the launcher has more room.
export const getLauncherPlacement = (position) => {
  if (!position) {
    return null;
  }
  const { clientWidth, clientHeight } = document.documentElement;
  const { right, bottom, width, height } = position;
  const left = clientWidth - right - width;
  const top = clientHeight - bottom - height;
  const opensBelow = top + height / 2 < clientHeight / 2;
  const alignsLeft = left + width / 2 < clientWidth / 2;
  return {
    '--launcher-left': alignsLeft ? `${left}px` : 'auto',
    '--launcher-right': alignsLeft ? 'auto' : `${right}px`,
    '--launcher-bottom': `${bottom}px`,
    '--launcher-align': alignsLeft ? 'flex-start' : 'flex-end',
    '--window-left': alignsLeft ? `${left}px` : 'auto',
    '--window-right': alignsLeft ? 'auto' : `${right}px`,
    '--window-top': opensBelow ? `${top + height + WINDOW_GAP_PX}px` : 'auto',
    '--window-bottom': opensBelow ? 'auto' : `${bottom + height + WINDOW_GAP_PX}px`,
    '--window-max-height': `${
      opensBelow ? clientHeight - top - height - 2 * WINDOW_GAP_PX - POWERED_BY_HEIGHT_PX : top - 2 * WINDOW_GAP_PX
    }px`,
    '--powered-by-left': alignsLeft ? 'auto' : `${POWERED_BY_INSET_PX}px`,
    '--powered-by-right': alignsLeft ? `${POWERED_BY_INSET_PX}px` : 'auto'
  };
};

const StaffToolsChat = () => {
  const { position, startDrag } = useFloatingPosition('staffToolsChat');
  const placement = getLauncherPlacement(position);
  const classes = useStyles({ placement });
  const session = useSession();
  const { pathname } = useLocation();
  const context = useMemo(() => getStaffToolsChatContext(pathname), [pathname]);
  const isEligible = Boolean(
    import.meta.env.VITE_STAFF_TOOLS_CHAT_ENABLED === 'true' &&
      session?.isLoggedIn &&
      session?.isInternalAgent &&
      !session?.isAgency &&
      session?.canViewTools &&
      session?.canUseStaffToolsChat
  );
  const shouldShow = isEligible && context !== null;
  const hasInjectedRef = useRef(false);
  const lastPayloadRef = useRef(null);
  const sdkRef = useRef(null);
  const unsubscribersRef = useRef([]);
  const isMountedRef = useRef(true);
  const currentRef = useRef(null);
  currentRef.current = { shouldShow, context: shouldShow ? context : NO_TOOL_CONTEXT };

  const synchronize = useCallback(() => {
    if (!isMountedRef.current || !sdkRef.current) {
      return;
    }
    const current = currentRef.current;
    if (typeof window.sendPayload === 'function') {
      const payload = JSON.stringify({
        action: 'context',
        bot_message: '',
        payload: { instructions: CONTEXT_INSTRUCTIONS, data: current.context }
      });
      if (payload !== lastPayloadRef.current) {
        lastPayloadRef.current = payload;
        window.sendPayload(payload);
      }
    }
    if (!current.shouldShow) {
      sdkRef.current.chat?.hide?.();
    }
  }, []);

  const handleReady = useCallback(
    (sdk) => {
      if (!isMountedRef.current || sdkRef.current === sdk) {
        return;
      }
      unsubscribersRef.current.forEach((unsubscribe) => unsubscribe());
      sdkRef.current = sdk;
      lastPayloadRef.current = null;
      unsubscribersRef.current = INDEMN_EVENTS.map((event) =>
        sdk.on(event, (payload = {}) => {
          const { context: currentContext } = currentRef.current;
          const { event: sdkEvent, timestamp, agentId, visitorId, scriptLoadedAt } = payload;
          track(`[Indemn] ${event}`, {
            event: sdkEvent,
            timestamp,
            agentId,
            visitorId,
            scriptLoadedAt,
            toolId: currentContext.toolId,
            view: currentContext.view
          });
          // chat:show is emitted inside an SDK Redux reducer. Dispatch only after it returns.
          // A new conversation needs the context again even when it hasn't changed.
          if (event === 'chat:show' || event === 'conversation:started') {
            lastPayloadRef.current = null;
            queueMicrotask(synchronize);
          }
        })
      );
      queueMicrotask(synchronize);
    },
    [synchronize]
  );

  useEffect(() => {
    if (!shouldShow || hasInjectedRef.current) {
      return;
    }
    hasInjectedRef.current = true;
    window.onIndemnReady = handleReady;
    if (window.Indemn) {
      handleReady(window.Indemn);
    }
    if (!document.querySelector(`script[src="${INDEMN_SCRIPT_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = INDEMN_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  }, [shouldShow, handleReady]);

  useEffect(() => {
    queueMicrotask(synchronize);
  }, [context, shouldShow, synchronize]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      unsubscribersRef.current.forEach((unsubscribe) => unsubscribe());
      if (window.onIndemnReady === handleReady) {
        delete window.onIndemnReady;
      }
    },
    [handleReady]
  );

  // The SDK renders the launcher inside this anchor, so drags are picked up by delegation.
  const handlePointerDown = (event) => {
    const launcher = event.target.closest?.('.rw-launcher');
    if (launcher) {
      startDrag(event, launcher.closest('.rw-launcher-container'));
    }
  };

  if (!hasInjectedRef.current && !shouldShow) {
    return null;
  }

  return (
    <div
      id="indemn-webchat"
      className={classNames(classes.anchor, { [classes.hidden]: !shouldShow, [classes.moved]: placement })}
      onPointerDown={handlePointerDown}
      data-params={JSON.stringify(currentRef.current.context)}
    />
  );
};

export default StaffToolsChat;
